import { useState } from "react";

import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { cn } from "../../../lib/utils";
import { getIcon } from "../../../utils/getIcon";
import { INTEGRATION_SCOPES, isNonOAuthIntegration } from "../../constants";
import { useFetchAllIntegrations } from "../../hooks/useFetchAllIntegrations";
import { getIntegrationDescription } from "../../utils";
import { IntegrationModal } from "../IntegrationModal";
import { ErrorState } from "../shared/ErrorState";

import type { Integration } from "../../types/integrations";

/**
 * One source at the deployment tier.
 *
 * The first connect reuses the shared modal unchanged — same terms acceptance, same
 * OAuth, same result step. Only the folder step is absent, because a platform account's
 * folders are chosen per workspace rather than for the account. Further accounts are
 * added from the source's own page, where the existing ones are visible.
 */
const DeploymentSourceCard = ({
  integration,
}: {
  integration: Integration;
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isConnected = integration.flag;

  return (
    <>
      <Card
        className="flex h-full flex-col overflow-hidden rounded-xl border shadow-sm transition-shadow duration-200 hover:shadow-md"
        data-testid={`deployment-source-card-${integration.label}`}
      >
        {isConnected && (
          <div
            className={cn(
              "h-1.5 w-full",
              integration.tokenValid
                ? "bg-green-500 dark:bg-green-600"
                : "bg-red-500/80",
            )}
          />
        )}
        <CardContent className="flex h-full flex-col p-4">
          <div className="mb-3 flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-muted">
              {getIcon(integration.label)}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-base font-semibold">
                {integration.value}
              </h3>
              {isConnected ? (
                <div
                  className={cn(
                    "flex items-center gap-1 text-xs font-semibold",
                    integration.tokenValid
                      ? "text-green-600 dark:text-green-500"
                      : "text-red-500",
                  )}
                >
                  <div
                    className={cn(
                      "h-2 w-2 rounded-full",
                      integration.tokenValid ? "bg-green-500" : "bg-red-500",
                    )}
                  />
                  {integration.tokenValid ? "Connected" : "Reconnect"}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  Not connected
                </div>
              )}
            </div>
          </div>
          <p className="mb-4 flex-1 text-sm leading-[18px] text-muted-foreground">
            {getIntegrationDescription(integration.label)}
          </p>
          <div>
            {isConnected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(integration.label)}
                data-testid={`deployment-source-${integration.label}-manage`}
              >
                <Settings className="mr-1 h-4 w-4" />
                Manage
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setIsModalOpen(true)}
                data-testid={`deployment-source-${integration.label}-connect`}
              >
                Connect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <IntegrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        integration={integration}
        integrationScope={INTEGRATION_SCOPES.DEPLOYMENT}
      />
    </>
  );
};

export const DeploymentSourcesList = () => {
  const {
    data: integrations,
    isLoading,
    isError,
    refetch,
  } = useFetchAllIntegrations(INTEGRATION_SCOPES.DEPLOYMENT);

  /*
  Telegram, WhatsApp and Azure Blob authenticate with credentials or an OTP rather than
  OAuth, and neither flow exists at deployment level — so they are left out entirely
  rather than listed as something that cannot be connected here.
  */
  const connectableSources = integrations?.filter(
    (integration) => !isNonOAuthIntegration(integration.label),
  );

  return (
    <div className="mx-auto w-full max-w-5xl px-8 py-10">
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">
          Deployment Integrations
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect a source once for the whole deployment, then choose which
          folders each workspace crawls.
        </p>
      </div>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <div
              key={index}
              className="flex h-40 flex-col rounded-xl border bg-card p-4 shadow-sm"
            >
              <div className="mb-3 flex items-start gap-4">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="mb-4 flex-1 space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <>
          <ErrorState
            title="Failed to load deployment integrations"
            className="mb-4 rounded-lg border bg-card p-6"
          />
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </>
      )}

      {!isLoading && !isError && connectableSources?.length === 0 && (
        <ErrorState
          title="No sources available"
          message="No source can currently be connected for the whole deployment."
          className="rounded-lg border bg-card p-6"
        />
      )}

      {!isLoading && !isError && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {connectableSources?.map((integration) => (
            <DeploymentSourceCard
              key={integration.label}
              integration={integration}
            />
          ))}
        </div>
      )}
    </div>
  );
};
